import React, { useState, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Badge } from "../components/ui/badge";
import {
  Triangle,
  Calculator,
  Info,
  BookOpen,
  Lightbulb,
  AlertCircle,
  Copy,
  Check,
  RotateCcw,
  Ruler,
  Compass,
  TrendingUp,
  Eye,
  EyeOff,
  Download,
  Share2,
  Sparkles,
  Zap,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import "@/App.css";

// Triangle solving functions with detailed steps
const solveSSS = (a, b, c) => {
  if (a + b <= c || a + c <= b || b + c <= a) return null;
  
  const A = Math.acos((b*b + c*c - a*a) / (2*b*c)) * 180 / Math.PI;
  const B = Math.acos((a*a + c*c - b*b) / (2*a*c)) * 180 / Math.PI;
  const C = 180 - A - B;
  
  const s = (a + b + c) / 2;
  const area = Math.sqrt(s * (s-a) * (s-b) * (s-c));
  const perimeter = a + b + c;
  
  // Heights
  const ha = (2 * area) / a;
  const hb = (2 * area) / b;
  const hc = (2 * area) / c;
  
  // Medians
  const ma = 0.5 * Math.sqrt(2*b*b + 2*c*c - a*a);
  const mb = 0.5 * Math.sqrt(2*a*a + 2*c*c - b*b);
  const mc = 0.5 * Math.sqrt(2*a*a + 2*b*b - c*c);
  
  // Radii
  const circumradius = (a * b * c) / (4 * area);
  const inradius = area / s;
  
  const steps = [
    `Given: sides a=${a}, b=${b}, c=${c}`,
    `Check triangle inequality: ${a}+${b}>${c}, ${a}+${c}>${b}, ${b}+${c}>${a} ✓`,
    `Using Law of Cosines: cos(A) = (b²+c²-a²)/(2bc)`,
    `A = arccos((${b}²+${c}²-${a}²)/(2×${b}×${c})) = ${A.toFixed(2)}°`,
    `B = arccos((${a}²+${c}²-${b}²)/(2×${a}×${c})) = ${B.toFixed(2)}°`,
    `C = 180° - A - B = ${C.toFixed(2)}°`,
    `Semi-perimeter: s = (a+b+c)/2 = ${s.toFixed(2)}`,
    `Area using Heron's formula: √[s(s-a)(s-b)(s-c)] = ${area.toFixed(2)}`
  ];
  
  return { a, b, c, A, B, C, area, perimeter, ha, hb, hc, ma, mb, mc, circumradius, inradius, steps, method: 'SSS' };
};

const solveSAS = (a, B, c) => {
  const b = Math.sqrt(a*a + c*c - 2*a*c*Math.cos(B * Math.PI / 180));
  const A = Math.asin(a * Math.sin(B * Math.PI / 180) / b) * 180 / Math.PI;
  const C = 180 - A - B;
  
  const s = (a + b + c) / 2;
  const area = Math.sqrt(s * (s-a) * (s-b) * (s-c));
  const perimeter = a + b + c;
  
  const ha = (2 * area) / a;
  const hb = (2 * area) / b;
  const hc = (2 * area) / c;
  
  const ma = 0.5 * Math.sqrt(2*b*b + 2*c*c - a*a);
  const mb = 0.5 * Math.sqrt(2*a*a + 2*c*c - b*b);
  const mc = 0.5 * Math.sqrt(2*a*a + 2*b*b - c*c);
  
  const circumradius = (a * b * c) / (4 * area);
  const inradius = area / s;
  
  const steps = [
    `Given: sides a=${a}, c=${c}, included angle B=${B}°`,
    `Using Law of Cosines: b² = a² + c² - 2ac×cos(B)`,
    `b = √(${a}² + ${c}² - 2×${a}×${c}×cos(${B}°)) = ${b.toFixed(2)}`,
    `Using Law of Sines: sin(A)/a = sin(B)/b`,
    `A = arcsin(a×sin(B)/b) = ${A.toFixed(2)}°`,
    `C = 180° - A - B = ${C.toFixed(2)}°`,
    `Area = (1/2)×a×c×sin(B) = ${area.toFixed(2)}`
  ];
  
  return { a, b, c, A, B, C, area, perimeter, ha, hb, hc, ma, mb, mc, circumradius, inradius, steps, method: 'SAS' };
};

const solveASA = (A, c, B) => {
  const C = 180 - A - B;
  if (C <= 0) return null;
  
  const a = c * Math.sin(A * Math.PI / 180) / Math.sin(C * Math.PI / 180);
  const b = c * Math.sin(B * Math.PI / 180) / Math.sin(C * Math.PI / 180);
  
  const s = (a + b + c) / 2;
  const area = Math.sqrt(s * (s-a) * (s-b) * (s-c));
  const perimeter = a + b + c;
  
  const ha = (2 * area) / a;
  const hb = (2 * area) / b;
  const hc = (2 * area) / c;
  
  const ma = 0.5 * Math.sqrt(2*b*b + 2*c*c - a*a);
  const mb = 0.5 * Math.sqrt(2*a*a + 2*c*c - b*b);
  const mc = 0.5 * Math.sqrt(2*a*a + 2*b*b - c*c);
  
  const circumradius = (a * b * c) / (4 * area);
  const inradius = area / s;
  
  const steps = [
    `Given: angles A=${A}°, B=${B}°, included side c=${c}`,
    `Calculate third angle: C = 180° - A - B = ${C.toFixed(2)}°`,
    `Using Law of Sines: a/sin(A) = b/sin(B) = c/sin(C)`,
    `a = c×sin(A)/sin(C) = ${c}×sin(${A}°)/sin(${C.toFixed(2)}°) = ${a.toFixed(2)}`,
    `b = c×sin(B)/sin(C) = ${c}×sin(${B}°)/sin(${C.toFixed(2)}°) = ${b.toFixed(2)}`,
    `Area = (1/2)×a×b×sin(C) = ${area.toFixed(2)}`
  ];
  
  return { a, b, c, A, B, C, area, perimeter, ha, hb, hc, ma, mb, mc, circumradius, inradius, steps, method: 'ASA' };
};

const solveAAS = (A, B, a) => {
  const C = 180 - A - B;
  if (C <= 0) return null;
  
  const b = a * Math.sin(B * Math.PI / 180) / Math.sin(A * Math.PI / 180);
  const c = a * Math.sin(C * Math.PI / 180) / Math.sin(A * Math.PI / 180);
  
  const s = (a + b + c) / 2;
  const area = Math.sqrt(s * (s-a) * (s-b) * (s-c));
  const perimeter = a + b + c;
  
  const ha = (2 * area) / a;
  const hb = (2 * area) / b;
  const hc = (2 * area) / c;
  
  const ma = 0.5 * Math.sqrt(2*b*b + 2*c*c - a*a);
  const mb = 0.5 * Math.sqrt(2*a*a + 2*c*c - b*b);
  const mc = 0.5 * Math.sqrt(2*a*a + 2*b*b - c*c);
  
  const circumradius = (a * b * c) / (4 * area);
  const inradius = area / s;
  
  const steps = [
    `Given: angles A=${A}°, B=${B}°, non-included side a=${a}`,
    `Calculate third angle: C = 180° - A - B = ${C.toFixed(2)}°`,
    `Using Law of Sines: a/sin(A) = b/sin(B) = c/sin(C)`,
    `b = a×sin(B)/sin(A) = ${a}×sin(${B}°)/sin(${A}°) = ${b.toFixed(2)}`,
    `c = a×sin(C)/sin(A) = ${a}×sin(${C.toFixed(2)}°)/sin(${A}°) = ${c.toFixed(2)}`,
    `Area = (1/2)×a×b×sin(C) = ${area.toFixed(2)}`
  ];
  
  return { a, b, c, A, B, C, area, perimeter, ha, hb, hc, ma, mb, mc, circumradius, inradius, steps, method: 'AAS' };
};

// Triangle Diagram Component
const TriangleDiagram = ({ result, showLabels = true }) => {
  if (!result) {
    return (
      <svg viewBox="0 0 300 250" className="w-full h-full max-w-[380px] mx-auto">
        <text x="150" y="125" fontSize="14" fill="hsl(var(--muted-foreground))" textAnchor="middle">
          Enter values to see triangle
        </text>
      </svg>
    );
  }
  
  const { a, b, c, A, B, C } = result;
  
  const scale = 180 / Math.max(a, b, c);
  const scaledA = a * scale;
  const scaledB = b * scale;
  const scaledC = c * scale;
  
  const x1 = 50;
  const y1 = 210;
  const x2 = x1 + scaledC;
  const y2 = y1;
  const x3 = x1 + scaledB * Math.cos(A * Math.PI / 180);
  const y3 = y1 - scaledB * Math.sin(A * Math.PI / 180);
  
  return (
    <svg viewBox="0 0 300 250" className="w-full h-full max-w-[380px] mx-auto">
      <defs>
        <filter id="triangleShadow">
          <feDropShadow dx="0" dy="2" stdDeviation="4" floodOpacity="0.2"/>
        </filter>
        <linearGradient id="triangleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="hsl(25, 95%, 53%)" stopOpacity="0.2" />
          <stop offset="100%" stopColor="hsl(43, 96%, 56%)" stopOpacity="0.05" />
        </linearGradient>
      </defs>
      
      {/* Triangle */}
      <polygon 
        points={`${x1},${y1} ${x2},${y2} ${x3},${y3}`}
        fill="url(#triangleGradient)"
        stroke="hsl(25, 95%, 53%)"
        strokeWidth="3"
        filter="url(#triangleShadow)"
      />
      
      {/* Vertices */}
      <circle cx={x1} cy={y1} r="6" fill="#ef4444" stroke="white" strokeWidth="2.5" />
      <circle cx={x2} cy={y2} r="6" fill="#3b82f6" stroke="white" strokeWidth="2.5" />
      <circle cx={x3} cy={y3} r="6" fill="#22c55e" stroke="white" strokeWidth="2.5" />
      
      {/* Vertex Labels */}
      {showLabels && (
        <>
          <text x={x1-18} y={y1+6} fontSize="15" fontWeight="700" fill="#ef4444">A</text>
          <text x={x2+12} y={y2+6} fontSize="15" fontWeight="700" fill="#3b82f6">B</text>
          <text x={x3} y={y3-12} fontSize="15" fontWeight="700" fill="#22c55e">C</text>
        </>
      )}
      
      {/* Side Labels */}
      {showLabels && (
        <>
          <text x={(x2+x3)/2 + 12} y={(y2+y3)/2} fontSize="13" fill="hsl(var(--foreground))" fontWeight="600">
            a={a.toFixed(2)}
          </text>
          <text x={(x1+x3)/2 - 35} y={(y1+y3)/2} fontSize="13" fill="hsl(var(--foreground))" fontWeight="600">
            b={b.toFixed(2)}
          </text>
          <text x={(x1+x2)/2} y={y1+22} fontSize="13" fill="hsl(var(--foreground))" fontWeight="600">
            c={c.toFixed(2)}
          </text>
        </>
      )}
      
      {/* Angle Arcs */}
      <path d={`M ${x1+20} ${y1} A 20 20 0 0 0 ${x1 + 20*Math.cos(A*Math.PI/180)} ${y1 - 20*Math.sin(A*Math.PI/180)}`} 
        stroke="#ef4444" strokeWidth="2" fill="none"/>
      <text x={x1+28} y={y1-10} fontSize="11" fill="#ef4444" fontWeight="600">{A.toFixed(1)}°</text>
      
      <path d={`M ${x2-20} ${y2} A 20 20 0 0 1 ${x2 - 20*Math.cos((180-B)*Math.PI/180)} ${y2 - 20*Math.sin((180-B)*Math.PI/180)}`} 
        stroke="#3b82f6" strokeWidth="2" fill="none"/>
      <text x={x2-40} y={y2-10} fontSize="11" fill="#3b82f6" fontWeight="600">{B.toFixed(1)}°</text>
      
      {/* Method Badge */}
      <text x="150" y="240" fontSize="13" fill="hsl(var(--muted-foreground))" fontWeight="600" textAnchor="middle">
        Method: {result.method}
      </text>
    </svg>
  );
};

// Copy Button
const CopyButton = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleCopy} title={`Copy ${label}`}>
      <AnimatePresence mode="wait">
        {copied ? (
          <motion.div key="check" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}>
            <Check className="w-3.5 h-3.5 text-green-500" />
          </motion.div>
        ) : (
          <motion.div key="copy" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}>
            <Copy className="w-3.5 h-3.5" />
          </motion.div>
        )}
      </AnimatePresence>
    </Button>
  );
};

// Property Card
const PropertyCard = ({ label, value, color, icon: Icon, formula }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    className={`rounded-xl p-4 ${color} border`}
  >
    <div className="flex items-start justify-between mb-2">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 opacity-70" />}
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
      </div>
      <CopyButton text={value} label={label} />
    </div>
    <div className="font-mono text-lg font-bold text-foreground mb-1">
      {value}
    </div>
    {formula && (
      <div className="text-xs text-muted-foreground font-medium mt-2 font-mono bg-background/50 px-2 py-1 rounded">
        {formula}
      </div>
    )}
  </motion.div>
);

function TriangleSolver() {
  const [solverType, setSolverType] = useState('SSS');
  const [inputs, setInputs] = useState({
    SSS: { a: 5, b: 6, c: 7 },
    SAS: { a: 5, B: 60, c: 6 },
    ASA: { A: 45, c: 10, B: 60 },
    AAS: { A: 45, B: 60, a: 7 }
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showFormulas, setShowFormulas] = useState(true);
  const [showSteps, setShowSteps] = useState(true);
  
  const result = useMemo(() => {
    try {
      switch(solverType) {
        case 'SSS':
          return solveSSS(inputs.SSS.a, inputs.SSS.b, inputs.SSS.c);
        case 'SAS':
          return solveSAS(inputs.SAS.a, inputs.SAS.B, inputs.SAS.c);
        case 'ASA':
          return solveASA(inputs.ASA.A, inputs.ASA.c, inputs.ASA.B);
        case 'AAS':
          return solveAAS(inputs.AAS.A, inputs.AAS.B, inputs.AAS.a);
        default:
          return null;
      }
    } catch (e) {
      return null;
    }
  }, [solverType, inputs]);
  
  const handleInputChange = (key, value) => {
    setInputs(prev => ({
      ...prev,
      [solverType]: { ...prev[solverType], [key]: parseFloat(value) || 0 }
    }));
  };
  
  const handleReset = useCallback(() => {
    setInputs({
      SSS: { a: 5, b: 6, c: 7 },
      SAS: { a: 5, B: 60, c: 6 },
      ASA: { A: 45, c: 10, B: 60 },
      AAS: { A: 45, B: 60, a: 7 }
    });
    setSolverType('SSS');
    setShowAdvanced(false);
  }, []);
  
  const handleExport = () => {
    if (!result) return;
    const data = {
      ...result,
      timestamp: new Date().toISOString()
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `triangle-${solverType}-${Date.now()}.json`;
    a.click();
  };
  
  const getTriangleType = () => {
    if (!result) return null;
    const { A, B, C, a, b, c } = result;
    
    const types = [];
    
    // By angles
    if (Math.abs(A - 90) < 0.01 || Math.abs(B - 90) < 0.01 || Math.abs(C - 90) < 0.01) {
      types.push('Right Triangle');
    } else if (A > 90 || B > 90 || C > 90) {
      types.push('Obtuse Triangle');
    } else {
      types.push('Acute Triangle');
    }
    
    // By sides
    const tolerance = 0.001;
    if (Math.abs(a - b) < tolerance && Math.abs(b - c) < tolerance) {
      types.push('Equilateral Triangle');
    } else if (Math.abs(a - b) < tolerance || Math.abs(b - c) < tolerance || Math.abs(a - c) < tolerance) {
      types.push('Isosceles Triangle');
    } else {
      types.push('Scalene Triangle');
    }
    
    return types;
  };
  
  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <Helmet>
        <title>Triangle Solver - SSS, SAS, ASA, AAS Calculator</title>
        <meta name="description" content="Professional triangle solver using SSS, SAS, ASA, and AAS methods. Calculate all triangle properties with step-by-step solutions." />
      </Helmet>
      
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg">
              <Triangle className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-foreground">Triangle Solver</h1>
              <p className="text-muted-foreground mt-1">Solve triangles with step-by-step solutions</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleExport} disabled={!result} className="gap-2">
              <Download className="w-4 h-4" />
              Export
            </Button>
            <Button variant="outline" size="sm" className="gap-2">
              <Share2 className="w-4 h-4" />
              Share
            </Button>
          </div>
        </div>
        
        {/* Quick Stats Bar */}
        {result && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-2 sm:grid-cols-4 gap-3"
          >
            <Card className="p-3 bg-orange-500/5 border-orange-500/20">
              <div className="text-xs text-muted-foreground mb-1">Method</div>
              <div className="text-lg font-bold text-orange-600">{result.method}</div>
            </Card>
            <Card className="p-3 bg-red-500/5 border-red-500/20">
              <div className="text-xs text-muted-foreground mb-1">Area</div>
              <div className="text-lg font-bold text-red-600">{result.area.toFixed(2)}</div>
            </Card>
            <Card className="p-3 bg-amber-500/5 border-amber-500/20">
              <div className="text-xs text-muted-foreground mb-1">Perimeter</div>
              <div className="text-lg font-bold text-amber-600">{result.perimeter.toFixed(2)}</div>
            </Card>
            <Card className="p-3 bg-rose-500/5 border-rose-500/20">
              <div className="text-xs text-muted-foreground mb-1">Type</div>
              <div className="text-sm font-bold text-rose-600">{getTriangleType()?.[0] || 'N/A'}</div>
            </Card>
          </motion.div>
        )}
      </motion.div>
      
      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Input Panel */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:sticky lg:top-24 lg:self-start space-y-6"
        >
          {/* Input Controls */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Calculator className="w-5 h-5 text-orange-600" />
                <span className="font-semibold text-foreground text-lg">Input Values</span>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-9 w-9 hover:bg-destructive/10 hover:text-destructive" 
                onClick={handleReset}
                title="Reset All"
              >
                <RotateCcw className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Solver Method Selection */}
            <div className="mb-6">
              <Label className="text-sm text-muted-foreground mb-3 block font-medium">Solving Method</Label>
              <Tabs value={solverType} onValueChange={setSolverType} className="w-full">
                <TabsList className="w-full grid grid-cols-4 h-auto">
                  <TabsTrigger value="SSS" className="text-xs py-2.5">
                    SSS
                  </TabsTrigger>
                  <TabsTrigger value="SAS" className="text-xs py-2.5">
                    SAS
                  </TabsTrigger>
                  <TabsTrigger value="ASA" className="text-xs py-2.5">
                    ASA
                  </TabsTrigger>
                  <TabsTrigger value="AAS" className="text-xs py-2.5">
                    AAS
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <div className="mt-3 p-3 bg-muted/50 rounded-lg text-xs text-muted-foreground">
                <strong>Current: </strong>
                {solverType === 'SSS' && 'Side-Side-Side (3 sides known)'}
                {solverType === 'SAS' && 'Side-Angle-Side (2 sides + included angle)'}
                {solverType === 'ASA' && 'Angle-Side-Angle (2 angles + included side)'}
                {solverType === 'AAS' && 'Angle-Angle-Side (2 angles + non-included side)'}
              </div>
            </div>
            
            {/* Input Fields */}
            <div className="space-y-5">
              {solverType === 'SSS' && (
                <>
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                    <Label className="text-sm mb-2 block font-medium">Side a</Label>
                    <Input
                      type="number"
                      value={inputs.SSS.a}
                      onChange={(e) => handleInputChange('a', e.target.value)}
                      className="h-12 text-lg font-mono"
                      min="0"
                      step="0.1"
                    />
                  </motion.div>
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
                    <Label className="text-sm mb-2 block font-medium">Side b</Label>
                    <Input
                      type="number"
                      value={inputs.SSS.b}
                      onChange={(e) => handleInputChange('b', e.target.value)}
                      className="h-12 text-lg font-mono"
                      min="0"
                      step="0.1"
                    />
                  </motion.div>
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                    <Label className="text-sm mb-2 block font-medium">Side c</Label>
                    <Input
                      type="number"
                      value={inputs.SSS.c}
                      onChange={(e) => handleInputChange('c', e.target.value)}
                      className="h-12 text-lg font-mono"
                      min="0"
                      step="0.1"
                    />
                  </motion.div>
                </>
              )}
              
              {solverType === 'SAS' && (
                <>
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                    <Label className="text-sm mb-2 block font-medium">Side a</Label>
                    <Input
                      type="number"
                      value={inputs.SAS.a}
                      onChange={(e) => handleInputChange('a', e.target.value)}
                      className="h-12 text-lg font-mono"
                      min="0"
                      step="0.1"
                    />
                  </motion.div>
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
                    <Label className="text-sm mb-2 block font-medium">Angle B (degrees)</Label>
                    <Input
                      type="number"
                      value={inputs.SAS.B}
                      onChange={(e) => handleInputChange('B', e.target.value)}
                      className="h-12 text-lg font-mono"
                      min="0"
                      max="180"
                      step="1"
                    />
                  </motion.div>
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                    <Label className="text-sm mb-2 block font-medium">Side c</Label>
                    <Input
                      type="number"
                      value={inputs.SAS.c}
                      onChange={(e) => handleInputChange('c', e.target.value)}
                      className="h-12 text-lg font-mono"
                      min="0"
                      step="0.1"
                    />
                  </motion.div>
                </>
              )}
              
              {solverType === 'ASA' && (
                <>
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                    <Label className="text-sm mb-2 block font-medium">Angle A (degrees)</Label>
                    <Input
                      type="number"
                      value={inputs.ASA.A}
                      onChange={(e) => handleInputChange('A', e.target.value)}
                      className="h-12 text-lg font-mono"
                      min="0"
                      max="180"
                      step="1"
                    />
                  </motion.div>
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
                    <Label className="text-sm mb-2 block font-medium">Side c</Label>
                    <Input
                      type="number"
                      value={inputs.ASA.c}
                      onChange={(e) => handleInputChange('c', e.target.value)}
                      className="h-12 text-lg font-mono"
                      min="0"
                      step="0.1"
                    />
                  </motion.div>
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                    <Label className="text-sm mb-2 block font-medium">Angle B (degrees)</Label>
                    <Input
                      type="number"
                      value={inputs.ASA.B}
                      onChange={(e) => handleInputChange('B', e.target.value)}
                      className="h-12 text-lg font-mono"
                      min="0"
                      max="180"
                      step="1"
                    />
                  </motion.div>
                </>
              )}
              
              {solverType === 'AAS' && (
                <>
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                    <Label className="text-sm mb-2 block font-medium">Angle A (degrees)</Label>
                    <Input
                      type="number"
                      value={inputs.AAS.A}
                      onChange={(e) => handleInputChange('A', e.target.value)}
                      className="h-12 text-lg font-mono"
                      min="0"
                      max="180"
                      step="1"
                    />
                  </motion.div>
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
                    <Label className="text-sm mb-2 block font-medium">Angle B (degrees)</Label>
                    <Input
                      type="number"
                      value={inputs.AAS.B}
                      onChange={(e) => handleInputChange('B', e.target.value)}
                      className="h-12 text-lg font-mono"
                      min="0"
                      max="180"
                      step="1"
                    />
                  </motion.div>
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                    <Label className="text-sm mb-2 block font-medium">Side a</Label>
                    <Input
                      type="number"
                      value={inputs.AAS.a}
                      onChange={(e) => handleInputChange('a', e.target.value)}
                      className="h-12 text-lg font-mono"
                      min="0"
                      step="0.1"
                    />
                  </motion.div>
                </>
              )}
            </div>
            
            {/* Validation Status */}
            <div className="mt-6">
              {result ? (
                <div className="flex items-center gap-2 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium text-green-600">Valid Triangle ✓</span>
                </div>
              ) : (
                <div className="flex items-start gap-2 p-3 bg-destructive/10 rounded-lg border border-destructive/20">
                  <XCircle className="w-5 h-5 text-destructive mt-0.5" />
                  <div>
                    <span className="text-sm font-medium text-destructive block">Invalid Triangle</span>
                    <span className="text-xs text-destructive/80">Check triangle inequality or angle sum</span>
                  </div>
                </div>
              )}
            </div>
            
            {/* Triangle Type Info */}
            {result && (
              <div className="mt-4 p-4 bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-xl border border-orange-500/20">
                <div className="text-center">
                  <span className="text-sm text-muted-foreground block mb-2">Triangle Classification</span>
                  {getTriangleType()?.map((type, i) => (
                    <Badge key={i} variant="secondary" className="m-1">{type}</Badge>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </motion.div>
        
        {/* Center/Right Column - Visualization & Results */}
        <div className="lg:col-span-2 space-y-6">
          {/* Visualization */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="rounded-2xl p-6 border-2 shadow-lg">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-semibold text-foreground flex items-center gap-2 text-lg">
                  <Compass className="w-5 h-5 text-orange-600" />
                  Interactive Triangle Diagram
                  <Badge variant="secondary" className="ml-2 text-xs">Real-time</Badge>
                </h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFormulas(!showFormulas)}
                  className="gap-2"
                >
                  {showFormulas ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  <span className="hidden sm:inline">Formulas</span>
                </Button>
              </div>
              
              <div className="rounded-xl p-6 bg-gradient-to-br from-orange-500/5 to-red-500/5 border-2 border-orange-500/20">
                <TriangleDiagram result={result} showLabels={true} />
              </div>
              
              {result && (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="p-3 rounded-lg bg-background border">
                    <span className="text-xs text-muted-foreground block mb-1">Angles</span>
                    <div className="space-y-0.5">
                      <div className="text-xs"><span className="text-red-500 font-semibold">A:</span> {result.A.toFixed(2)}°</div>
                      <div className="text-xs"><span className="text-blue-500 font-semibold">B:</span> {result.B.toFixed(2)}°</div>
                      <div className="text-xs"><span className="text-green-500 font-semibold">C:</span> {result.C.toFixed(2)}°</div>
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-background border">
                    <span className="text-xs text-muted-foreground block mb-1">Sides</span>
                    <div className="space-y-0.5">
                      <div className="text-xs font-mono">a: {result.a.toFixed(3)}</div>
                      <div className="text-xs font-mono">b: {result.b.toFixed(3)}</div>
                      <div className="text-xs font-mono">c: {result.c.toFixed(3)}</div>
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-background border col-span-2 sm:col-span-1">
                    <span className="text-xs text-muted-foreground block mb-1">Properties</span>
                    <div className="space-y-0.5">
                      <div className="text-xs">Area: {result.area.toFixed(2)}</div>
                      <div className="text-xs">Perimeter: {result.perimeter.toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          </motion.div>
          
          {/* Step-by-Step Solution */}
          {result && (
            <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-blue-500/5 to-cyan-500/5">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-semibold text-foreground flex items-center gap-2 text-lg">
                  <BookOpen className="w-5 h-5 text-blue-600" />
                  Step-by-Step Solution
                  <Sparkles className="w-4 h-4 text-yellow-500 ml-auto" />
                </h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSteps(!showSteps)}
                  className="gap-2"
                >
                  {showSteps ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  <span className="hidden sm:inline">{showSteps ? 'Hide' : 'Show'}</span>
                </Button>
              </div>
              
              <AnimatePresence>
                {showSteps && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-3"
                  >
                    {result.steps.map((step, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="flex gap-3 p-3 bg-background/60 rounded-lg border"
                      >
                        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-foreground font-mono">{step}</p>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </Card>
          )}
          
          {/* Basic Properties */}
          {result && (
            <Card className="rounded-2xl p-6 border-2 shadow-lg">
              <h2 className="font-semibold text-foreground flex items-center gap-2 mb-5 text-lg">
                <Info className="w-5 h-5 text-orange-600" />
                Triangle Properties
              </h2>
              
              <div className="space-y-6">
                {/* Angles */}
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground mb-3">Angles</h3>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <PropertyCard
                      label="Angle A"
                      value={`${result.A.toFixed(6)}°`}
                      color="bg-red-500/10 border-red-500/20"
                      formula={showFormulas ? "Opposite to side a" : null}
                    />
                    <PropertyCard
                      label="Angle B"
                      value={`${result.B.toFixed(6)}°`}
                      color="bg-blue-500/10 border-blue-500/20"
                      formula={showFormulas ? "Opposite to side b" : null}
                    />
                    <PropertyCard
                      label="Angle C"
                      value={`${result.C.toFixed(6)}°`}
                      color="bg-green-500/10 border-green-500/20"
                      formula={showFormulas ? "Opposite to side c" : null}
                    />
                  </div>
                </div>
                
                {/* Sides */}
                <div className="pt-4 border-t border-border">
                  <h3 className="text-sm font-semibold text-muted-foreground mb-3">Sides</h3>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <PropertyCard
                      label="Side a"
                      value={result.a.toFixed(6)}
                      color="bg-orange-500/10 border-orange-500/20"
                      formula={showFormulas ? "Opposite to angle A" : null}
                    />
                    <PropertyCard
                      label="Side b"
                      value={result.b.toFixed(6)}
                      color="bg-amber-500/10 border-amber-500/20"
                      formula={showFormulas ? "Opposite to angle B" : null}
                    />
                    <PropertyCard
                      label="Side c"
                      value={result.c.toFixed(6)}
                      color="bg-yellow-500/10 border-yellow-500/20"
                      formula={showFormulas ? "Opposite to angle C" : null}
                    />
                  </div>
                </div>
                
                {/* Area & Perimeter */}
                <div className="pt-4 border-t border-border">
                  <h3 className="text-sm font-semibold text-muted-foreground mb-3">Area & Perimeter</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <PropertyCard
                      label="Area"
                      value={result.area.toFixed(6)}
                      color="bg-purple-500/10 border-purple-500/20"
                      formula={showFormulas ? "Heron's formula" : null}
                    />
                    <PropertyCard
                      label="Perimeter"
                      value={result.perimeter.toFixed(6)}
                      color="bg-pink-500/10 border-pink-500/20"
                      formula={showFormulas ? "P = a+b+c" : null}
                    />
                  </div>
                </div>
              </div>
            </Card>
          )}
          
          {/* Advanced Properties */}
          {result && (
            <div>
              <Button
                variant="outline"
                className="w-full mb-4 gap-2"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                {showAdvanced ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                {showAdvanced ? 'Hide' : 'Show'} Advanced Properties
                <Zap className="w-4 h-4 ml-auto text-yellow-500" />
              </Button>
              
              <AnimatePresence>
                {showAdvanced && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-green-500/5 to-emerald-500/5">
                      <h2 className="font-semibold text-foreground flex items-center gap-2 mb-5 text-lg">
                        <Sparkles className="w-5 h-5 text-green-600" />
                        Advanced Properties
                      </h2>
                      
                      <div className="space-y-6">
                        {/* Heights */}
                        <div>
                          <h3 className="text-sm font-semibold text-muted-foreground mb-3">Heights (Altitudes)</h3>
                          <div className="grid sm:grid-cols-3 gap-4">
                            <PropertyCard
                              label="Height to side a"
                              value={result.ha.toFixed(6)}
                              color="bg-teal-500/10 border-teal-500/20"
                              formula={showFormulas ? "h = 2A/a" : null}
                            />
                            <PropertyCard
                              label="Height to side b"
                              value={result.hb.toFixed(6)}
                              color="bg-cyan-500/10 border-cyan-500/20"
                              formula={showFormulas ? "h = 2A/b" : null}
                            />
                            <PropertyCard
                              label="Height to side c"
                              value={result.hc.toFixed(6)}
                              color="bg-sky-500/10 border-sky-500/20"
                              formula={showFormulas ? "h = 2A/c" : null}
                            />
                          </div>
                        </div>
                        
                        {/* Medians */}
                        <div className="pt-4 border-t border-border">
                          <h3 className="text-sm font-semibold text-muted-foreground mb-3">Medians</h3>
                          <div className="grid sm:grid-cols-3 gap-4">
                            <PropertyCard
                              label="Median ma"
                              value={result.ma.toFixed(6)}
                              color="bg-lime-500/10 border-lime-500/20"
                              formula={showFormulas ? "√(2b²+2c²-a²)/2" : null}
                            />
                            <PropertyCard
                              label="Median mb"
                              value={result.mb.toFixed(6)}
                              color="bg-green-500/10 border-green-500/20"
                              formula={showFormulas ? "√(2a²+2c²-b²)/2" : null}
                            />
                            <PropertyCard
                              label="Median mc"
                              value={result.mc.toFixed(6)}
                              color="bg-emerald-500/10 border-emerald-500/20"
                              formula={showFormulas ? "√(2a²+2b²-c²)/2" : null}
                            />
                          </div>
                        </div>
                        
                        {/* Radii */}
                        <div className="pt-4 border-t border-border">
                          <h3 className="text-sm font-semibold text-muted-foreground mb-3">Circle Radii</h3>
                          <div className="grid sm:grid-cols-2 gap-4">
                            <PropertyCard
                              label="Circumradius (R)"
                              value={result.circumradius.toFixed(6)}
                              color="bg-violet-500/10 border-violet-500/20"
                              formula={showFormulas ? "R = abc/(4A)" : null}
                            />
                            <PropertyCard
                              label="Inradius (r)"
                              value={result.inradius.toFixed(6)}
                              color="bg-purple-500/10 border-purple-500/20"
                              formula={showFormulas ? "r = A/s" : null}
                            />
                          </div>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
          
          {/* Complete Formulas Reference */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg">
            <h2 className="text-xl font-bold text-foreground mb-5 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-orange-600" />
              Complete Triangle Formulas Reference
            </h2>
            
            <div className="space-y-5">
              {/* Law of Sines */}
              <div className="p-5 rounded-xl bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20">
                <h3 className="font-bold text-blue-600 mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Law of Sines
                </h3>
                <div className="bg-background/60 p-4 rounded-lg">
                  <code className="font-mono text-sm block text-center">
                    a/sin(A) = b/sin(B) = c/sin(C) = 2R
                  </code>
                  <p className="text-xs text-muted-foreground mt-3">
                    The ratio of each side to the sine of its opposite angle is constant and equals twice the circumradius. Used for ASA and AAS cases.
                  </p>
                </div>
              </div>
              
              {/* Law of Cosines */}
              <div className="p-5 rounded-xl bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20">
                <h3 className="font-bold text-green-600 mb-3 flex items-center gap-2">
                  <Compass className="w-4 h-4" />
                  Law of Cosines
                </h3>
                <div className="space-y-2">
                  <div className="bg-background/60 p-3 rounded-lg">
                    <code className="font-mono text-sm">a² = b² + c² - 2bc×cos(A)</code>
                  </div>
                  <div className="bg-background/60 p-3 rounded-lg">
                    <code className="font-mono text-sm">b² = a² + c² - 2ac×cos(B)</code>
                  </div>
                  <div className="bg-background/60 p-3 rounded-lg">
                    <code className="font-mono text-sm">c² = a² + b² - 2ab×cos(C)</code>
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">
                    Generalizes the Pythagorean theorem to any triangle. Used for SSS and SAS cases.
                  </p>
                </div>
              </div>
              
              {/* Area Formulas */}
              <div className="p-5 rounded-xl bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20">
                <h3 className="font-bold text-purple-600 mb-3">Area Formulas</h3>
                <div className="space-y-2 text-sm">
                  <div className="bg-background/60 p-3 rounded-lg">
                    <div className="font-semibold mb-1">Heron's Formula</div>
                    <code className="font-mono text-xs">A = √[s(s-a)(s-b)(s-c)]</code>
                    <p className="text-xs text-muted-foreground mt-1">where s = (a+b+c)/2</p>
                  </div>
                  <div className="bg-background/60 p-3 rounded-lg">
                    <div className="font-semibold mb-1">Using Two Sides and Included Angle</div>
                    <code className="font-mono text-xs">A = (1/2)×a×b×sin(C)</code>
                  </div>
                  <div className="bg-background/60 p-3 rounded-lg">
                    <div className="font-semibold mb-1">Using Base and Height</div>
                    <code className="font-mono text-xs">A = (1/2)×base×height</code>
                  </div>
                </div>
              </div>
              
              {/* Additional Formulas */}
              <div className="p-5 rounded-xl bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-500/20">
                <h3 className="font-bold text-orange-600 mb-3">Additional Important Formulas</h3>
                <div className="space-y-2 text-sm">
                  <div className="bg-background/60 p-3 rounded-lg">
                    <div className="font-semibold mb-1">Angle Sum</div>
                    <code className="font-mono text-xs">A + B + C = 180°</code>
                  </div>
                  <div className="bg-background/60 p-3 rounded-lg">
                    <div className="font-semibold mb-1">Height</div>
                    <code className="font-mono text-xs">h = 2A/base</code>
                  </div>
                  <div className="bg-background/60 p-3 rounded-lg">
                    <div className="font-semibold mb-1">Median</div>
                    <code className="font-mono text-xs">m = (1/2)√(2b²+2c²-a²)</code>
                  </div>
                  <div className="bg-background/60 p-3 rounded-lg">
                    <div className="font-semibold mb-1">Circumradius</div>
                    <code className="font-mono text-xs">R = abc/(4A)</code>
                  </div>
                  <div className="bg-background/60 p-3 rounded-lg">
                    <div className="font-semibold mb-1">Inradius</div>
                    <code className="font-mono text-xs">r = A/s</code>
                  </div>
                </div>
              </div>
            </div>
          </Card>
          
          {/* Comprehensive FAQ */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg">
            <h2 className="text-xl font-bold text-foreground mb-5 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-yellow-500" />
              Frequently Asked Questions
            </h2>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="faq-1" className="border-b border-border">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium text-foreground">What is the triangle inequality theorem?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    The triangle inequality states that the sum of the lengths of any two sides of a triangle must be greater than the length of the third side. This fundamental property must be satisfied for three line segments to form a valid triangle.
                  </p>
                  <div className="bg-muted/30 p-4 rounded-lg">
                    <p className="text-sm font-semibold mb-2">Three conditions must all be true:</p>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li>• a + b {`>`} c</li>
                      <li>• a + c {`>`} b</li>
                      <li>• b + c {`>`} a</li>
                    </ul>
                  </div>
                  <p className="text-sm text-muted-foreground mt-3">
                    <strong>Example:</strong> Sides of length 3, 4, and 10 cannot form a triangle because 3 + 4 = 7, which is not greater than 10.
                  </p>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-2" className="border-b border-border">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium text-foreground">When should I use Law of Cosines vs Law of Sines?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4">
                    <div className="bg-green-500/10 p-4 rounded-lg border border-green-500/20">
                      <h4 className="font-semibold text-sm mb-2 text-green-600">Use Law of Cosines when:</h4>
                      <ul className="space-y-1 text-sm text-muted-foreground">
                        <li>• You know all three sides (SSS)</li>
                        <li>• You know two sides and the included angle (SAS)</li>
                        <li>• You need to find an unknown angle</li>
                      </ul>
                    </div>
                    <div className="bg-blue-500/10 p-4 rounded-lg border border-blue-500/20">
                      <h4 className="font-semibold text-sm mb-2 text-blue-600">Use Law of Sines when:</h4>
                      <ul className="space-y-1 text-sm text-muted-foreground">
                        <li>• You know two angles and one side (ASA or AAS)</li>
                        <li>• You need to find an unknown side</li>
                        <li>• You already know one angle-side pair</li>
                      </ul>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      <strong>Tip:</strong> The Law of Cosines is more general but computationally heavier. The Law of Sines is simpler when you have angle information.
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-3" className="border-b border-border">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium text-foreground">What is Heron's formula and why is it useful?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    Heron's formula calculates the area of a triangle when you know all three sides, without needing to know any angles or heights. It's named after Hero of Alexandria, a Greek mathematician.
                  </p>
                  <div className="bg-purple-500/10 p-4 rounded-lg border border-purple-500/20 mb-3">
                    <p className="text-sm font-semibold mb-2">The Formula:</p>
                    <code className="font-mono text-sm block mb-2">A = √[s(s-a)(s-b)(s-c)]</code>
                    <p className="text-xs text-muted-foreground">where s = (a+b+c)/2 is the semi-perimeter</p>
                  </div>
                  <div className="bg-muted/30 p-4 rounded-lg">
                    <p className="text-sm font-semibold mb-2">Example:</p>
                    <p className="text-xs text-muted-foreground">
                      For a triangle with sides 3, 4, and 5:<br/>
                      s = (3+4+5)/2 = 6<br/>
                      A = √[6(6-3)(6-4)(6-5)] = √[6×3×2×1] = √36 = 6 square units
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-4" className="border-b border-border">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium text-foreground">What are medians and altitudes?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4">
                    <div className="bg-cyan-500/10 p-4 rounded-lg border border-cyan-500/20">
                      <h4 className="font-semibold text-sm mb-2 text-cyan-600">Median</h4>
                      <p className="text-sm text-muted-foreground mb-2">
                        A line segment from a vertex to the midpoint of the opposite side. Every triangle has three medians, and they all intersect at a single point called the centroid (center of mass).
                      </p>
                      <code className="font-mono text-xs bg-background/60 p-2 rounded block">
                        Median formula: m = (1/2)√(2b²+2c²-a²)
                      </code>
                    </div>
                    <div className="bg-teal-500/10 p-4 rounded-lg border border-teal-500/20">
                      <h4 className="font-semibold text-sm mb-2 text-teal-600">Altitude (Height)</h4>
                      <p className="text-sm text-muted-foreground mb-2">
                        A perpendicular line segment from a vertex to the opposite side (or its extension). Altitudes are used to calculate area: Area = (1/2) × base × height.
                      </p>
                      <code className="font-mono text-xs bg-background/60 p-2 rounded block">
                        Height formula: h = 2A/base
                      </code>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      <strong>Key difference:</strong> Medians connect to midpoints; altitudes are perpendicular to sides.
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-5" className="border-b border-border">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium text-foreground">What are circumradius and inradius?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4">
                    <div className="bg-violet-500/10 p-4 rounded-lg border border-violet-500/20">
                      <h4 className="font-semibold text-sm mb-2 text-violet-600">Circumradius (R)</h4>
                      <p className="text-sm text-muted-foreground mb-2">
                        The radius of the circumscribed circle (circumcircle) that passes through all three vertices of the triangle. Every triangle has exactly one circumcircle.
                      </p>
                      <code className="font-mono text-xs bg-background/60 p-2 rounded block">
                        R = abc/(4A)
                      </code>
                    </div>
                    <div className="bg-purple-500/10 p-4 rounded-lg border border-purple-500/20">
                      <h4 className="font-semibold text-sm mb-2 text-purple-600">Inradius (r)</h4>
                      <p className="text-sm text-muted-foreground mb-2">
                        The radius of the inscribed circle (incircle) that touches all three sides of the triangle from the inside. The center of this circle is called the incenter.
                      </p>
                      <code className="font-mono text-xs bg-background/60 p-2 rounded block">
                        r = A/s (where s is semi-perimeter)
                      </code>
                    </div>
                    <div className="bg-indigo-500/10 p-4 rounded-lg border border-indigo-500/20">
                      <p className="text-sm text-indigo-600 font-semibold mb-1">Special case: Right Triangle</p>
                      <p className="text-xs text-muted-foreground">
                        For a right triangle: R = c/2 (half the hypotenuse) and r = (a+b-c)/2
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-6" className="border-0">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium text-foreground">How do I classify triangles?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-sm mb-3">Classification by Angles:</h4>
                      <div className="grid gap-2">
                        <div className="bg-background p-3 rounded-lg border">
                          <span className="font-semibold text-sm">Acute Triangle:</span>
                          <span className="text-sm text-muted-foreground ml-2">All angles &lt; 90°</span>
                        </div>
                        <div className="bg-background p-3 rounded-lg border">
                          <span className="font-semibold text-sm">Right Triangle:</span>
                          <span className="text-sm text-muted-foreground ml-2">One angle = 90°</span>
                        </div>
                        <div className="bg-background p-3 rounded-lg border">
                          <span className="font-semibold text-sm">Obtuse Triangle:</span>
                          <span className="text-sm text-muted-foreground ml-2">One angle &gt; 90°</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm mb-3">Classification by Sides:</h4>
                      <div className="grid gap-2">
                        <div className="bg-background p-3 rounded-lg border">
                          <span className="font-semibold text-sm">Equilateral:</span>
                          <span className="text-sm text-muted-foreground ml-2">All sides equal (all angles = 60°)</span>
                        </div>
                        <div className="bg-background p-3 rounded-lg border">
                          <span className="font-semibold text-sm">Isosceles:</span>
                          <span className="text-sm text-muted-foreground ml-2">Two sides equal (two angles equal)</span>
                        </div>
                        <div className="bg-background p-3 rounded-lg border">
                          <span className="font-semibold text-sm">Scalene:</span>
                          <span className="text-sm text-muted-foreground ml-2">All sides different (all angles different)</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </Card>
          
          {/* Real-world Applications */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-orange-500/5 to-red-500/5">
            <h2 className="text-xl font-bold text-foreground mb-5 flex items-center gap-2">
              <Zap className="w-5 h-5 text-orange-600" />
              Real-World Applications
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <span className="text-2xl">🏗️</span>
                  Engineering & Construction
                </h3>
                <p className="text-xs text-muted-foreground">
                  Roof truss design, bridge construction, structural support calculations, and determining distances in surveying and land measurement.
                </p>
              </div>
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <span className="text-2xl">🛰️</span>
                  Navigation & GPS
                </h3>
                <p className="text-xs text-muted-foreground">
                  Triangulation for GPS positioning, calculating distances between points, aviation flight planning, and maritime navigation.
                </p>
              </div>
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <span className="text-2xl">🎮</span>
                  Computer Graphics
                </h3>
                <p className="text-xs text-muted-foreground">
                  3D modeling (all objects are made of triangles), game development, animation, ray tracing, and rendering calculations.
                </p>
              </div>
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <span className="text-2xl">📐</span>
                  Physics & Astronomy
                </h3>
                <p className="text-xs text-muted-foreground">
                  Force vector analysis, calculating distances to celestial objects, projectile motion, and wave interference patterns.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default TriangleSolver;
